import styled from 'styled-components';
import { COLORS, monoValue } from '../../lib/theme';
import { TIMELAPSE_RANGES, TIMELAPSE_SPEEDS } from '../CameraFeed/useTimelapse';

// A second strip under the panel header, so the header keeps its shape and
// the controls sit outside the video box where pointer events are ordinary.
const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  padding: 5px 10px;
  background: ${COLORS.headerFill};
  border-bottom: 1px solid ${COLORS.hairline};
  flex: 0 0 auto;
`;

const Group = styled.div`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${COLORS.hairline};
`;

// Same idiom as ModeSwitch: the selected segment inverts to steel, no yellow.
const Segment = styled.button`
  ${monoValue}
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 2px 7px;
  cursor: pointer;
  border: 0;
  color: ${(p) => (p.$selected ? COLORS.ground : COLORS.label)};
  background: ${(p) => (p.$selected ? COLORS.text : 'transparent')};

  & + & {
    border-left: 1px solid ${COLORS.hairline};
  }

  &:hover:not(:disabled) {
    color: ${(p) => (p.$selected ? COLORS.ground : COLORS.text)};
  }

  &:disabled {
    color: ${COLORS.hairline};
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${COLORS.ground}, 0 0 0 4px ${COLORS.accent};
  }
`;

const GroupLabel = styled.span`
  ${monoValue}
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.label};
  padding: 2px 6px;
  border-right: 1px solid ${COLORS.hairline};
  white-space: nowrap;
`;

const Scrub = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 120px;
  min-width: 0;
`;

// A hairline track with a square steel thumb -- the stock control is the
// wrong shape for this HUD in every browser.
const Slider = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  height: 12px;
  margin: 0;
  cursor: pointer;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-runnable-track {
    height: 1px;
    background: ${COLORS.plateBorder};
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 8px;
    height: 8px;
    margin-top: -3.5px;
    border: 0;
    border-radius: 0;
    background: ${COLORS.text};
  }
  &::-moz-range-track {
    height: 1px;
    background: ${COLORS.plateBorder};
  }
  &::-moz-range-thumb {
    width: 8px;
    height: 8px;
    border: 0;
    border-radius: 0;
    background: ${COLORS.text};
  }

  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${COLORS.ground}, 0 0 0 4px ${COLORS.accent};
  }
`;

const Position = styled.span`
  ${monoValue}
  font-size: 9px;
  color: ${COLORS.label};
  white-space: nowrap;
  min-width: 5.5em;
  text-align: right;
`;

/**
 * The header button that opens and closes a panel's timelapse. Selected
 * state matches the other header segments.
 */
export function TimelapseToggle({ on, onToggle }) {
  return (
    <Group>
      <Segment
        type="button"
        aria-pressed={on}
        $selected={on}
        onClick={onToggle}
        title="Play the stored stills as a timelapse"
      >
        Lapse
      </Segment>
    </Group>
  );
}

/**
 * Play/pause, scrub, range and speed for one camera's timelapse. Everything
 * here changes which stored still is on screen and nothing else -- there is
 * no capture control anywhere in this app.
 */
export default function TimelapseBar({ timelapse }) {
  const { playing, play, pause, seek, index, count, rangeKey, setRange, fps, setFps } = timelapse;
  const empty = count === 0;

  return (
    <Bar aria-label="Timelapse controls">
      <Group>
        <Segment
          type="button"
          onClick={playing ? pause : play}
          disabled={empty}
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
        >
          {playing ? '\u275A\u275A' : '\u25B6'}
        </Segment>
      </Group>

      <Scrub>
        <Slider
          type="range"
          min={0}
          max={Math.max(0, count - 1)}
          value={empty ? 0 : index}
          disabled={empty}
          onChange={(e) => {
            pause();
            seek(Number(e.target.value));
          }}
          aria-label="Timelapse position"
        />
        <Position>{empty ? 'No frames' : `${index + 1}/${count}`}</Position>
      </Scrub>

      <Group role="radiogroup" aria-label="Timelapse range">
        <GroupLabel>Last</GroupLabel>
        {TIMELAPSE_RANGES.map((r) => (
          <Segment
            key={r.key}
            type="button"
            role="radio"
            aria-checked={rangeKey === r.key}
            $selected={rangeKey === r.key}
            onClick={() => setRange(r.key)}
          >
            {r.key}
          </Segment>
        ))}
      </Group>

      <Group role="radiogroup" aria-label="Timelapse speed">
        <GroupLabel>FPS</GroupLabel>
        {TIMELAPSE_SPEEDS.map((s) => (
          <Segment
            key={s}
            type="button"
            role="radio"
            aria-checked={fps === s}
            $selected={fps === s}
            onClick={() => setFps(s)}
          >
            {s}
          </Segment>
        ))}
      </Group>
    </Bar>
  );
}
